import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import {
  FaChevronLeft as FaChevronLeftRaw,
  FaChevronRight as FaChevronRightRaw,
  FaComments as FaCommentsRaw,
  FaEdit as FaEditRaw,
  FaEye as FaEyeRaw,
  FaFileAlt as FaFileAltRaw,
  FaPlus as FaPlusRaw,
  FaSearch as FaSearchRaw,
  FaTrashAlt as FaTrashAltRaw,
} from 'react-icons/fa';
import { teacherService, type TeacherGradeRow, type TeacherWorkItem } from '../../services/classroom/teacherService';
import { parseWorkDescription } from './assignmentWorkContent';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaChevronLeft: any = FaChevronLeftRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaChevronRight: any = FaChevronRightRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaComments: any = FaCommentsRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEdit: any = FaEditRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEye: any = FaEyeRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaFileAlt: any = FaFileAltRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaPlus: any = FaPlusRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaSearch: any = FaSearchRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaTrashAlt: any = FaTrashAltRaw;

const MOBILE_BREAKPOINT = '960px';
const PAGE_SIZE = 4;

type TabKey = 'active' | 'scheduled' | 'draft' | 'archived';
type ContentKind = 'paper' | 'discussion';
type SortKey = 'dueAsc' | 'dueDesc' | 'titleAsc';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
`;

const TopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const PageTitle = styled.h2`
  margin: 0 0 0.35rem;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.35rem, 2.2vw, 1.65rem);
  font-weight: 700;
  color: var(--text);
`;

const PageSubtitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  line-height: 1.45;
  max-width: 38rem;
`;

const MetricsAndCta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.75rem;
`;

const StatCard = styled.div<{ $accent: 'primary' | 'warning' | 'deep' }>`
  min-width: 128px;
  padding: 0.75rem 1rem 0.75rem 0.85rem;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  border-left-width: 4px;
  border-left-color: ${({ $accent }) =>
    $accent === 'primary' ? 'var(--primary)' : $accent === 'warning' ? 'var(--warning, #e8a23c)' : 'var(--primaryDark)'};
`;

const StatLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const StatValue = styled.div`
  margin-top: 0.2rem;
  font-family: 'Poppins', sans-serif;
  font-size: 1.45rem;
  font-weight: 700;
  color: var(--text);
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
  border: none;
  border-radius: 10px;
  background: var(--primary);
  color: var(--onPrimary);
  cursor: pointer;
  align-self: center;
  &:hover {
    background: var(--primaryDark);
  }
`;

const TabsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.35rem;
`;

const TabBtn = styled.button<{ $active: boolean }>`
  padding: 0.45rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? 'var(--outline)' : 'transparent')};
  background: ${({ $active }) => ($active ? 'var(--surface)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--text)' : 'var(--textSecondary, var(--onSurfaceVariant))')};
  cursor: pointer;
  &:hover {
    color: var(--primary);
  }
`;

const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  padding: 0.85rem 1rem;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 180px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.75rem;
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
  border-radius: 10px;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-size: 0.875rem;
  color: var(--text);
  outline: none;
  font-family: 'Open Sans', sans-serif;
  &::placeholder {
    color: var(--textSecondary, var(--onSurfaceVariant));
  }
`;

const Select = styled.select`
  padding: 0.45rem 0.65rem;
  font-size: 0.82rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--text);
  font-family: 'Open Sans', sans-serif;
  cursor: pointer;
`;

const ShowingCount = styled.span`
  margin-left: auto;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--textSecondary, var(--onSurfaceVariant));
  white-space: nowrap;
`;

const AssignmentRow = styled.div<{ $draft?: boolean }>`
  display: grid;
  grid-template-columns: auto minmax(0, 1.2fr) minmax(120px, 0.7fr) minmax(100px, 0.55fr) auto auto;
  gap: 0.65rem 0.85rem;
  align-items: center;
  padding: 0.9rem 1rem;
  background: var(--surface);
  border: ${({ $draft }) => ($draft ? '2px dashed var(--outline)' : '1px solid var(--outline)')};
  border-radius: 12px;
  margin-bottom: 0.55rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto auto;
  }
`;

const TypeIcon = styled.div<{ $muted?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $muted }) => ($muted ? 'var(--surfaceVariant)' : 'var(--primaryContainer, var(--surfaceVariant))')};
  color: ${({ $muted }) => ($muted ? 'var(--textSecondary, var(--onSurfaceVariant))' : 'var(--primary)')};
`;

const MainInfo = styled.div`
  min-width: 0;
`;

const RowTitle = styled.div`
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  color: var(--text);
`;

const MetaLine = styled.div`
  margin-top: 0.25rem;
  font-size: 0.8rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const KindTag = styled.span<{ $k: ContentKind | 'draft' }>`
  display: inline-block;
  margin-left: 0.35rem;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  padding: 0.15rem 0.45rem;
  border-radius: 6px;
  vertical-align: middle;
  ${({ $k }) =>
    $k === 'discussion'
      ? `background: var(--tertiaryContainer, var(--surfaceVariant)); color: var(--primary);`
      : $k === 'draft'
        ? `background: var(--surfaceVariant); color: var(--textSecondary, var(--onSurfaceVariant));`
        : `background: var(--primaryContainer, var(--surfaceVariant)); color: var(--primary);`}
`;

const ColHead = styled.div`
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: var(--textSecondary, var(--onSurfaceVariant));
  margin-bottom: 0.3rem;
`;

const ProgressTrack = styled.div`
  height: 5px;
  border-radius: 999px;
  background: var(--surfaceVariant);
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number; $done?: boolean }>`
  width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
  height: 100%;
  border-radius: 999px;
  background: ${({ $done }) => ($done ? 'var(--success, #1a8f5c)' : 'var(--primary)')};
`;

const ProgressSub = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--textSecondary, var(--onSurfaceVariant));
  margin-top: 0.25rem;
`;

const DueText = styled.div`
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text);
`;

const StatusPill = styled.div<{ $variant: 'grade' | 'graded' | 'progress' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  background: var(--surfaceVariant);
  color: var(--text);
  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: ${({ $variant }) =>
      $variant === 'grade' ? 'var(--error, #d32f2f)' : $variant === 'graded' ? 'var(--success, #1a8f5c)' : 'var(--primary)'};
  }
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--textSecondary, var(--onSurfaceVariant));
  cursor: pointer;
  &:hover {
    background: var(--surfaceVariant);
    color: var(--primary);
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.1rem;
  justify-self: end;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-column: 1 / -1;
    justify-self: end;
  }
`;

const ProgressCell = styled.div`
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-column: 1 / -1;
  }
`;
const DueCell = styled.div`
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-column: 1 / -1;
  }
`;
const StatusCell = styled.div`
  justify-self: end;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-column: 1 / -1;
    justify-self: start;
  }
`;

const DraftBanner = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1rem;
  padding-top: 0.35rem;
  margin-top: 0.15rem;
  border-top: 1px dashed var(--outline);
  font-size: 0.82rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const PublishLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 0.82rem;
  font-weight: 800;
  color: var(--primary);
  cursor: pointer;
  font-family: 'Open Sans', sans-serif;
  &:hover {
    text-decoration: underline;
  }
`;

const FooterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0;
  font-size: 0.82rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const PageNumBtn = styled.button<{ $active?: boolean }>`
  min-width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? 'var(--primary)' : 'var(--outline)')};
  background: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--text)')};
  font-weight: 600;
  cursor: pointer;
  font-family: 'Open Sans', sans-serif;
  &:hover:not(:disabled) {
    border-color: var(--primary);
    color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--primary)')};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PreviewPanel = styled.div`
  margin-top: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--surfaceVariant);
  border-radius: 10px;
  font-size: 0.85rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

function endOfDay(d: Date): number {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.getTime();
}

function bucket(row: TeacherWorkItem): TabKey {
  if (row.archived) return 'archived';
  if (!row.published_at) return 'draft';
  if (!row.due_at) return 'active';
  const t = new Date(row.due_at).getTime();
  if (Number.isNaN(t)) return 'active';
  if (t > endOfDay(new Date())) return 'scheduled';
  return 'active';
}

function contentKind(row: TeacherWorkItem): ContentKind {
  const s = `${row.title}`.toLowerCase();
  if (s.includes('discuss') || s.includes('forum')) return 'discussion';
  return 'paper';
}

function kindLabel(k: ContentKind): string {
  return k === 'discussion' ? 'DISCUSSION' : 'PAPER';
}

function rowWorkflowStatus(
  row: TeacherWorkItem,
  sub: number,
  total: number,
  graded: number
): 'grade' | 'graded' | 'progress' {
  if (!row.published_at) return 'progress';
  if (sub > 0 && graded >= sub) return 'graded';
  const dueAt = row.due_at ? new Date(row.due_at).getTime() : Number.NaN;
  if (!Number.isNaN(dueAt) && dueAt <= Date.now() && sub > 0) return 'grade';
  const pct = total > 0 ? sub / total : 0;
  if (pct >= 1) return 'graded';
  if (pct >= 0.72) return 'grade';
  return 'progress';
}

function isFinalizedSubmissionStatus(status: TeacherGradeRow['status']): boolean {
  return status === 'submitted' || status === 'graded' || status === 'returned';
}

function statusLabel(v: 'grade' | 'graded' | 'progress'): string {
  if (v === 'grade') return 'Ready to grade';
  if (v === 'graded') return 'Graded';
  return 'In progress';
}

function statusPriority(status: TeacherGradeRow['status']): number {
  if (status === 'graded' || status === 'returned') return 3;
  if (status === 'submitted') return 2;
  return 1;
}

function rowTimestamp(iso?: string | null): number {
  if (!iso) return 0;
  const ts = new Date(iso).getTime();
  return Number.isNaN(ts) ? 0 : ts;
}

const ClassroomTeacherAssignmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabKey>('active');
  const [search, setSearch] = useState('');
  const [courseId, setCourseId] = useState('all');
  const [typeFilter, setTypeFilter] = useState<'all' | ContentKind>('all');
  const [sort, setSort] = useState<SortKey>('dueAsc');
  const [page, setPage] = useState(1);
  const [previewId, setPreviewId] = useState<string | null>(null);

  const [workQuery, overviewQuery, gradesQuery] = useQueries({
    queries: [
      { queryKey: ['classroom', 'teacher', 'work', 'assignment'], queryFn: () => teacherService.getWork('assignment') },
      { queryKey: ['classroom', 'teacher', 'overview'], queryFn: teacherService.getOverview },
      { queryKey: ['classroom', 'teacher', 'grades'], queryFn: teacherService.getGrades },
    ],
  });

  const classesData = overviewQuery.data;
  const classOptions = useMemo(() => classesData ?? [], [classesData]);

  const classNameById = useMemo(() => {
    const m = new Map<string, string>();
    classOptions.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [classOptions]);

  const rosterByClassId = useMemo(() => {
    const m = new Map<string, number>();
    classOptions.forEach((c) => m.set(c.id, c.activeStudents));
    return m;
  }, [classOptions]);

  const saveMutation = useMutation({
    mutationFn: teacherService.saveWork,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'work', 'assignment'] });
    },
  });

  const publishMutation = useMutation({
    mutationFn: (workId: string) => teacherService.publishWork('assignment', workId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'work', 'assignment'] });
    },
  });

  const filtered = useMemo(() => {
    let list = [...(workQuery.data ?? [])];
    list = list.filter((row) => bucket(row) === tab);
    if (courseId !== 'all') list = list.filter((r) => r.class_id === courseId);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((r) => r.title.toLowerCase().includes(q));
    if (typeFilter !== 'all') list = list.filter((r) => contentKind(r) === typeFilter);
    list.sort((a, b) => {
      if (sort === 'titleAsc') return a.title.localeCompare(b.title);
      const ta = a.due_at ? new Date(a.due_at).getTime() : Number.POSITIVE_INFINITY;
      const tb = b.due_at ? new Date(b.due_at).getTime() : Number.POSITIVE_INFINITY;
      if (sort === 'dueAsc') return ta - tb;
      return tb - ta;
    });
    return list;
  }, [workQuery.data, tab, courseId, search, typeFilter, sort]);

  const totalFiltered = filtered.length;
  const pageCount = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paged = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);

  useEffect(() => {
    setPage(1);
  }, [tab, courseId, typeFilter, search, sort]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const tabCounts = useMemo(() => {
    const rows = workQuery.data ?? [];
    const count = (t: TabKey) => rows.filter((r) => bucket(r) === t).length;
    return {
      active: count('active'),
      scheduled: count('scheduled'),
      draft: count('draft'),
      archived: count('archived'),
    };
  }, [workQuery.data]);

  const metrics = useMemo(() => {
    const rows = workQuery.data ?? [];
    const activeAssignments = rows.filter((r) => bucket(r) === 'active').length;
    const grades = (gradesQuery.data ?? []).filter((g) => g.work_type !== 'quiz');
    const pendingGrading = grades.filter((g) => g.status === 'submitted').length;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const submissionsToday = grades.filter((g) => {
      if (!g.submitted_at) return false;
      const submittedAt = new Date(g.submitted_at).getTime();
      if (Number.isNaN(submittedAt)) return false;
      if (submittedAt < startOfToday.getTime()) return false;
      return g.status === 'submitted' || g.status === 'graded' || g.status === 'returned';
    }).length;
    return {
      active: activeAssignments,
      submissionsToday,
      pendingGrading,
    };
  }, [workQuery.data, gradesQuery.data]);

  const assignmentProgressById = useMemo(() => {
    const assignments = workQuery.data ?? [];
    const grades = (gradesQuery.data ?? []).filter((g) => g.work_type !== 'quiz');
    const knownAssignmentIds = new Set(assignments.map((row) => row.id));
    const assignmentIdByClassAndTitle = new Map<string, string>();
    const classIdByName = new Map<string, string>();

    assignments.forEach((row) => {
      const key = `${row.class_id}::${row.title.trim().toLowerCase()}`;
      if (!assignmentIdByClassAndTitle.has(key)) {
        assignmentIdByClassAndTitle.set(key, row.id);
      }
    });
    classOptions.forEach((row) => {
      const normalized = row.name.trim().toLowerCase();
      if (!normalized || classIdByName.has(normalized)) return;
      classIdByName.set(normalized, row.id);
    });

    const latestByAssignment = new Map<string, Map<string, TeacherGradeRow>>();
    grades.forEach((gradeRow) => {
      let assignmentId = gradeRow.work_id && knownAssignmentIds.has(gradeRow.work_id) ? gradeRow.work_id : null;
      const classId = gradeRow.class_id ?? classIdByName.get(gradeRow.class_name?.trim().toLowerCase() ?? '');
      if (!assignmentId && classId && gradeRow.work_title) {
        assignmentId = assignmentIdByClassAndTitle.get(`${classId}::${gradeRow.work_title.trim().toLowerCase()}`) ?? null;
      }
      if (!assignmentId) return;

      const studentKey = gradeRow.student_id?.trim()
        ? `id:${gradeRow.student_id.trim()}`
        : gradeRow.student_name.trim()
          ? `name:${gradeRow.student_name.trim().toLowerCase()}`
          : null;
      if (!studentKey) return;

      const assignmentRows = latestByAssignment.get(assignmentId) ?? new Map<string, TeacherGradeRow>();
      const prev = assignmentRows.get(studentKey);
      if (!prev) {
        assignmentRows.set(studentKey, gradeRow);
      } else {
        const prevIsMissing = prev.status === 'missing';
        const currIsMissing = gradeRow.status === 'missing';
        if (prevIsMissing !== currIsMissing) {
          assignmentRows.set(studentKey, currIsMissing ? prev : gradeRow);
          latestByAssignment.set(assignmentId, assignmentRows);
          return;
        }
        const prevTs = rowTimestamp(prev.submitted_at);
        const currentTs = rowTimestamp(gradeRow.submitted_at);
        if (currentTs > prevTs) {
          assignmentRows.set(studentKey, gradeRow);
        } else if (currentTs === prevTs && statusPriority(gradeRow.status) > statusPriority(prev.status)) {
          assignmentRows.set(studentKey, gradeRow);
        }
      }
      latestByAssignment.set(assignmentId, assignmentRows);
    });

    const summaryByAssignment = new Map<string, { sub: number; total: number; pct: number; graded: number }>();
    assignments.forEach((row) => {
      const latestRows = Array.from(latestByAssignment.get(row.id) ?? new Map<string, TeacherGradeRow>()).map(([, value]) => value);
      const sub = latestRows.filter((gradeRow) => isFinalizedSubmissionStatus(gradeRow.status)).length;
      const graded = latestRows.filter((gradeRow) => gradeRow.status === 'graded' || gradeRow.status === 'returned').length;
      const rosterTotal = Math.max(0, rosterByClassId.get(row.class_id) ?? 0);
      const total = rosterTotal > 0 ? rosterTotal : latestRows.length;
      const pct = total > 0 ? Math.round((sub / total) * 100) : 0;
      summaryByAssignment.set(row.id, { sub, total, pct, graded });
    });

    return summaryByAssignment;
  }, [gradesQuery.data, rosterByClassId, workQuery.data, classOptions]);

  const startEdit = useCallback(
    (row: TeacherWorkItem) => {
      setPreviewId(null);
      navigate(`/classroom-teacher/assignments/${row.id}/edit`);
    },
    [navigate]
  );

  const goLive = (row: TeacherWorkItem) => {
    publishMutation.mutate(row.id);
  };

  const archiveRow = (row: TeacherWorkItem) => {
    saveMutation.mutate({ ...row, archived: true, type: 'assignment' });
  };

  if (workQuery.isLoading || overviewQuery.isLoading) {
    return <p style={{ color: 'var(--textSecondary, var(--onSurfaceVariant))' }}>Loading assignments…</p>;
  }

  if (workQuery.isError || overviewQuery.isError) {
    return <p style={{ color: 'var(--textSecondary, var(--onSurfaceVariant))' }}>Unable to load assignments.</p>;
  }

  const from = totalFiltered === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const to = Math.min(safePage * PAGE_SIZE, totalFiltered);

  return (
    <Page>
      <TopRow>
        <TitleBlock>
          <PageTitle>Assignments</PageTitle>
          <PageSubtitle>Create, schedule, and track assignments across your courses. Students see items once you go live.</PageSubtitle>
        </TitleBlock>
        <MetricsAndCta>
          <StatCard $accent="primary">
            <StatLabel>ACTIVE</StatLabel>
            <StatValue>{metrics.active}</StatValue>
          </StatCard>
          <StatCard $accent="warning">
            <StatLabel>TODAY&apos;S SUBMISSIONS</StatLabel>
            <StatValue>{metrics.submissionsToday}</StatValue>
          </StatCard>
          <StatCard $accent="deep">
            <StatLabel>PENDING GRADING</StatLabel>
            <StatValue>{metrics.pendingGrading}</StatValue>
          </StatCard>
          <PrimaryBtn
            type="button"
            onClick={() => {
              setPreviewId(null);
              navigate('/classroom-teacher/assignments/new');
            }}
          >
            <FaPlus size={14} aria-hidden />
            Create new
          </PrimaryBtn>
        </MetricsAndCta>
      </TopRow>

      <TabsRow>
        {(
          [
            ['active', 'Active'],
            ['scheduled', 'Scheduled'],
            ['draft', 'Draft'],
            ['archived', 'Archived'],
          ] as const
        ).map(([key, label]) => (
          <TabBtn key={key} type="button" $active={tab === key} onClick={() => setTab(key)}>
            {label}
            {key === 'active'
              ? ` (${tabCounts.active})`
              : key === 'scheduled'
                ? ` (${tabCounts.scheduled})`
                : key === 'draft'
                  ? ` (${tabCounts.draft})`
                  : ` (${tabCounts.archived})`}
          </TabBtn>
        ))}
      </TabsRow>

      <FiltersBar>
        <SearchWrap>
          <FaSearch size={14} aria-hidden />
          <SearchInput placeholder="Search resources…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </SearchWrap>
        <Select value={courseId} onChange={(e) => setCourseId(e.target.value)} aria-label="Course">
          <option value="all">All courses</option>
          {classOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as 'all' | ContentKind)} aria-label="Type">
          <option value="all">All types</option>
          <option value="paper">Paper</option>
          <option value="discussion">Discussion</option>
        </Select>
        <Select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label="Sort">
          <option value="dueAsc">Due date (soonest)</option>
          <option value="dueDesc">Due date (latest)</option>
          <option value="titleAsc">Title (A–Z)</option>
        </Select>
        <ShowingCount>
          Showing {totalFiltered} assignment{totalFiltered === 1 ? '' : 's'}
        </ShowingCount>
      </FiltersBar>

      {totalFiltered === 0 ? (
        <p style={{ color: 'var(--textSecondary, var(--onSurfaceVariant))' }}>
          No assignments in this view. Try another tab or create a new assignment.
        </p>
      ) : null}

      {paged.map((row) => {
        const isDraft = !row.published_at && !row.archived;
        const defaultTotal = Math.max(0, rosterByClassId.get(row.class_id) ?? 0);
        const progress = assignmentProgressById.get(row.id) ?? { sub: 0, total: defaultTotal, pct: 0, graded: 0 };
        const { sub, total, pct, graded } = progress;
        const wf = rowWorkflowStatus(row, sub, total, graded);
        const course = classNameById.get(row.class_id) ?? 'Course';
        const kind = contentKind(row);
        const showPreview = previewId === row.id;
        const parsedDescription = parseWorkDescription(row.description);
        const questionCount = parsedDescription.questions.length;

        return (
          <React.Fragment key={row.id}>
            <AssignmentRow $draft={isDraft}>
              <TypeIcon $muted={isDraft}>{kind === 'discussion' ? <FaComments size={16} /> : <FaFileAlt size={16} />}</TypeIcon>
              <MainInfo>
                <RowTitle>
                  {row.title}
                  <KindTag $k={isDraft ? 'draft' : kind}>{isDraft ? 'DRAFT' : kindLabel(kind)}</KindTag>
                </RowTitle>
                <MetaLine>
                  {course} · Module · {row.points} pts
                </MetaLine>
              </MainInfo>
              {!isDraft ? (
                <>
                  <ProgressCell>
                    <ColHead>PROGRESS</ColHead>
                    <ProgressTrack>
                      <ProgressFill $pct={pct} $done={wf === 'graded'} />
                    </ProgressTrack>
                    <ProgressSub>
                      {total > 0 ? `${sub}/${total} submitted` : `${sub} submitted`}
                    </ProgressSub>
                  </ProgressCell>
                  <DueCell>
                    <ColHead>DUE DATE</ColHead>
                    <DueText>
                      {row.due_at
                        ? new Date(row.due_at).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })
                        : '—'}
                    </DueText>
                  </DueCell>
                  <StatusCell>
                    <StatusPill $variant={wf}>{statusLabel(wf)}</StatusPill>
                  </StatusCell>
                </>
              ) : (
                <>
                  <ProgressCell>
                    <ColHead>STATUS</ColHead>
                    <DueText style={{ fontWeight: 700 }}>Not live</DueText>
                  </ProgressCell>
                  <DueCell>
                    <ColHead>TARGET DATE</ColHead>
                    <DueText>—</DueText>
                  </DueCell>
                  <StatusCell />
                </>
              )}
              <Actions>
                <IconBtn
                  type="button"
                  title={showPreview ? 'Hide' : 'View'}
                  onClick={() => setPreviewId(showPreview ? null : row.id)}
                >
                  <FaEye size={14} />
                </IconBtn>
                <IconBtn type="button" title="Edit" onClick={() => startEdit(row)}>
                  <FaEdit size={14} />
                </IconBtn>
                <IconBtn type="button" title="Archive" onClick={() => archiveRow(row)}>
                  <FaTrashAlt size={14} />
                </IconBtn>
              </Actions>
              {isDraft ? (
                <DraftBanner>
                  <span>Hidden from students until you go live.</span>
                  <PublishLink type="button" onClick={() => goLive(row)} disabled={publishMutation.isPending}>
                    Go live
                  </PublishLink>
                </DraftBanner>
              ) : row.published_at ? (
                <DraftBanner style={{ borderStyle: 'solid', background: 'color-mix(in srgb, var(--success, #1a8f5c) 8%, var(--surface))' }}>
                  <span>
                    Live since{' '}
                    {new Date(row.published_at).toLocaleString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </span>
                </DraftBanner>
              ) : null}
            </AssignmentRow>
            {showPreview ? (
              <PreviewPanel style={{ marginTop: '-0.4rem', marginBottom: '0.55rem' }}>
                <strong>{row.title}</strong> · {row.points} points
                <br />
                {questionCount} question{questionCount === 1 ? '' : 's'}
                {row.due_at ? (
                  <>
                    <br />
                    Due {new Date(row.due_at).toLocaleString()}
                  </>
                ) : (
                  <>
                    <br />
                    Not live — students cannot see this assignment yet.
                  </>
                )}
              </PreviewPanel>
            ) : null}
          </React.Fragment>
        );
      })}

      <FooterBar>
        <span>
          Showing {from} to {to} of {totalFiltered} assignments
        </span>
        <Pager>
          <PageNumBtn
            type="button"
            aria-label="Previous page"
            disabled={safePage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <FaChevronLeft size={12} />
          </PageNumBtn>
          {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
            <PageNumBtn key={n} type="button" $active={n === safePage} onClick={() => setPage(n)}>
              {n}
            </PageNumBtn>
          ))}
          <PageNumBtn
            type="button"
            aria-label="Next page"
            disabled={safePage >= pageCount}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          >
            <FaChevronRight size={12} />
          </PageNumBtn>
        </Pager>
      </FooterBar>
    </Page>
  );
};

export default ClassroomTeacherAssignmentsPage;
